import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Bot, History, Plus, Send, Sparkles } from 'lucide-react';

import { Button } from '@/shared/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet';
import { Textarea } from '@/shared/components/ui/textarea';
import { CollaborationBanner } from '@/shared/components/CollaborationBanner';
import { useHasModule } from '@/shared/hooks/use-entitlements';
import { useTrackModuleUsage } from '@/shared/hooks/use-track-module-usage';
import { cn } from '@/shared/lib/utils';

import { useAssistantChat } from '../hooks/use-assistant-chat';

export function AssistantChatWidget(): JSX.Element | null {
  const { t } = useTranslation();
  const hasAssistant = useHasModule('ai_assistant');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const chat = useAssistantChat();

  useTrackModuleUsage(open ? 'ai_assistant' : null);

  useEffect(() => {
    if (open && !chat.conversationId) {
      void chat.startNewConversation();
    }
  }, [open, chat.conversationId, chat.startNewConversation]);

  useEffect(() => {
    if (showHistory) void chat.loadConversations();
  }, [showHistory, chat.loadConversations]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [chat.messages, chat.streamingText, chat.pendingTool]);

  if (!hasAssistant) return null;

  const submit = (): void => {
    const content = draft.trim();
    if (!content || chat.isBusy) return;
    setDraft('');
    void chat.sendMessage(content);
  };

  return (
    <>
      <Button
        type="button"
        size="icon"
        onClick={() => setOpen(true)}
        aria-label={t('assistant.openLabel')}
        className="fixed bottom-6 right-6 z-40 size-12 rounded-full shadow-lg"
      >
        <Sparkles className="size-5" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          <SheetHeader className="border-b py-3 pl-4 pr-10">
            <div className="flex items-center justify-between gap-2">
              <SheetTitle className="flex items-center gap-2">
                <Bot className="size-4" />
                {t('assistant.title')}
              </SheetTitle>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" title={t('assistant.history')} onClick={() => setShowHistory((v) => !v)}>
                  <History className="size-4" />
                </Button>
                <Button variant="ghost" size="icon" title={t('assistant.newConversation')} onClick={() => void chat.startNewConversation()}>
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          {showHistory ? (
            <div className="max-h-40 overflow-y-auto border-b px-4 py-2">
              {chat.conversations.length === 0 ? (
                <p className="py-2 text-xs text-muted-foreground">{t('assistant.noHistory')}</p>
              ) : (
                chat.conversations.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      void chat.openConversation(c.id);
                      setShowHistory(false);
                    }}
                    className={cn(
                      'block w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                      c.id === chat.conversationId && 'bg-muted font-medium',
                    )}
                  >
                    {c.title ?? t('assistant.untitledConversation')}
                  </button>
                ))
              )}
            </div>
          ) : null}

          <div className="px-4 pt-3">
            <CollaborationBanner moduleKey="ai_assistant" variant="new" />
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {chat.messages
              .filter((m) => m.role !== 'tool' && m.content)
              .map((m) => (
                <div
                  key={m.id}
                  className={cn(
                    'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                    m.role === 'user' ? 'ml-auto bg-primary text-primary-foreground' : 'bg-muted',
                  )}
                >
                  {m.content}
                </div>
              ))}

            {chat.streamingText ? (
              <div className="max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm whitespace-pre-wrap">
                {chat.streamingText}
              </div>
            ) : null}

            {chat.pendingTool ? (
              <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm text-warning-text">
                <p>{chat.pendingTool.preview}</p>
                <div className="flex gap-2">
                  <Button onClick={() => void chat.confirmPendingTool()}>
                    {t('assistant.confirmExecute')}
                  </Button>
                  <Button variant="ghost" onClick={chat.dismissPendingTool}>
                    {t('assistant.cancel')}
                  </Button>
                </div>
              </div>
            ) : null}

            {chat.error ? (
              <div className="rounded-lg border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {chat.error}
              </div>
            ) : null}

            {chat.isBusy && !chat.streamingText && !chat.pendingTool ? (
              <p className="text-xs text-muted-foreground">{t('assistant.thinking')}</p>
            ) : null}
          </div>

          <div className="border-t p-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                aria-label={t('assistant.placeholder')} placeholder={t('assistant.placeholder')}
                rows={2}
                className="resize-none"
                disabled={chat.isBusy || Boolean(chat.pendingTool)}
              />
              <Button
                type="button"
                size="icon"
                onClick={submit}
                disabled={chat.isBusy || Boolean(chat.pendingTool) || !draft.trim()}
                aria-label={t('assistant.send')}
              >
                <Send className="size-4" />
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {t('assistant.thirdPartyNotice')}{' '}
              <Link to="/privacy-policy" target="_blank" className="underline underline-offset-4 hover:text-foreground">
                {t('assistant.thirdPartyNoticeLink')}
              </Link>
              .
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
